// /admin/analytics — headline numbers, DAU chart, visits-per-place ranking.
//
// Kept simple: three fetches, small CSS bar chart for DAU, table for
// visits ranking. Adds a Days selector so staff can look at 7/30/90-day
// windows without extra endpoints (the query takes the days param).

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../lib/api';

interface Overview {
    users_total:    number;
    dau_today:      number;
    dau_last_7:     number;
    visits_last_7:  number;
    places_total:   number;
    places_claimed: number;
    reviews_total:  number;
    avg_rating:     number;
}
interface DAUBucket { day: string; users: number; }
interface PlaceVisitCount {
    group_id: string; group_slug: string; group_name: string; category: string;
    visits: number; unique_users: number; total_minutes: number;
}
interface CommunityDAU {
    community_id: string; community_slug: string; community_name: string;
    dau: number; signals: number;
}

export function AdminAnalytics() {
    const api = useApi();
    const [days, setDays] = useState(30);
    const [ov,     setOv]     = useState<Overview | null>(null);
    const [dau,    setDau]    = useState<DAUBucket[]>([]);
    const [places, setPlaces] = useState<PlaceVisitCount[]>([]);
    const [byComm, setByComm] = useState<CommunityDAU[]>([]);

    const load = useCallback(async () => {
        try {
            const [o, d, p, c] = await Promise.all([
                api.get<Overview>('/api/admin/analytics/overview'),
                api.get<{ buckets: DAUBucket[] }>('/api/admin/analytics/dau?days=' + days),
                api.get<{ places: PlaceVisitCount[] }>('/api/admin/analytics/visits-per-place?days=' + days + '&limit=50'),
                api.get<{ communities: CommunityDAU[] }>('/api/admin/analytics/dau-per-community?days=' + days),
            ]);
            setOv(o);
            setDau(d.buckets ?? []);
            setPlaces(p.places ?? []);
            setByComm(c.communities ?? []);
        } catch (e) { console.error('analytics load', e); }
    }, [api, days]);
    useEffect(() => { load(); }, [load]);

    const maxDAU = Math.max(1, ...dau.map(b => b.users));

    return (
        <main style={{ padding: '20px 24px 60px', maxWidth: 1100, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                <h1 style={{ margin: 0, fontFamily: 'Newsreader,serif', fontSize: 28 }}>Analytics</h1>
            </div>
            <nav style={adminNav}>
                <Link to="/admin"           style={navLink}>Overview</Link>
                <Link to="/admin/ai-agents" style={navLink}>AI Agents</Link>
                <Link to="/admin/newsroom"  style={navLink}>Newsroom</Link>
                <Link to="/admin/map"       style={navLink}>Map</Link>
                <Link to="/admin/claims"    style={navLink}>Claims</Link>
                <Link to="/admin/analytics" style={navLink}>Analytics</Link>
            </nav>

            {ov && (
                <section style={cardsGrid}>
                    <Card label="Total users"        value={ov.users_total} />
                    <Card label="DAU today"          value={ov.dau_today} />
                    <Card label="DAU last 7 days"    value={ov.dau_last_7} />
                    <Card label="Visits last 7 days" value={ov.visits_last_7} />
                    <Card label="Places listed"      value={ov.places_total} />
                    <Card label="Places claimed"     value={ov.places_claimed} />
                    <Card label="Reviews"            value={ov.reviews_total} />
                    <Card label="Average rating"     value={ov.avg_rating ? ov.avg_rating.toFixed(2) : '—'} />
                </section>
            )}

            <div style={{ margin: '18px 0 8px', display: 'flex', gap: 10, alignItems: 'center', fontSize: 12, color: '#666' }}>
                <span>Window:</span>
                {[7, 30, 90].map(d => (
                    <button key={d} type="button" onClick={() => setDays(d)}
                            style={segBtn(days === d)}>{d}d</button>
                ))}
            </div>

            <section style={block}>
                <h2 style={h2}>Daily active users</h2>
                {dau.length === 0 ? (
                    <em style={{ color: '#999' }}>No signals yet.</em>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 140, borderBottom: '1px solid #cfc9b7', padding: '10px 0' }}>
                        {dau.map(b => (
                            <div key={b.day} title={`${new Date(b.day).toLocaleDateString()}: ${b.users}`}
                                 style={{
                                    flex: 1,
                                    height: `${(b.users / maxDAU) * 100}%`,
                                    background: 'var(--accent)',
                                    minHeight: b.users > 0 ? 2 : 0,
                                 }} />
                        ))}
                    </div>
                )}
                <div style={{ fontSize: 10, color: '#888', marginTop: 6, textAlign: 'right' }}>
                    max {maxDAU}
                </div>
            </section>

            <section style={block}>
                <h2 style={h2}>DAU per community (last {days} days)</h2>
                {byComm.length === 0 ? (
                    <em style={{ color: '#999' }}>No community-scoped activity yet.</em>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: '-apple-system,sans-serif', fontSize: 12 }}>
                        <thead>
                            <tr style={{ background: '#faf8f2', textAlign: 'left' }}>
                                <th style={th}>Community</th>
                                <th style={th}>DAU</th>
                                <th style={th}>Signals</th>
                            </tr>
                        </thead>
                        <tbody>
                            {byComm.map(c => (
                                <tr key={c.community_id || c.community_slug} style={{ borderBottom: '1px dotted #cfc9b7' }}>
                                    <td style={td}>
                                        {c.community_id
                                            ? <Link to={'/community/' + c.community_slug}>{c.community_name}</Link>
                                            : <em>{c.community_name}</em>}
                                    </td>
                                    <td style={td}>{c.dau}</td>
                                    <td style={td}>{c.signals}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            <section style={block}>
                <h2 style={h2}>Visits per place ({places.length} in window)</h2>
                {places.length === 0 ? (
                    <em style={{ color: '#999' }}>No place visits recorded yet — users need to opt in to "nudge me to review" in Settings.</em>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: '-apple-system,sans-serif', fontSize: 12 }}>
                        <thead>
                            <tr style={{ background: '#faf8f2', textAlign: 'left' }}>
                                <th style={th}>Place</th>
                                <th style={th}>Category</th>
                                <th style={th}>Visits</th>
                                <th style={th}>Unique users</th>
                                <th style={th}>Total mins</th>
                            </tr>
                        </thead>
                        <tbody>
                            {places.map(p => (
                                <tr key={p.group_id} style={{ borderBottom: '1px dotted #cfc9b7' }}>
                                    <td style={td}>
                                        <Link to={'/groups/' + p.group_slug + '/dashboard'}>{p.group_name}</Link>
                                    </td>
                                    <td style={td}>{p.category}</td>
                                    <td style={td}>{p.visits}</td>
                                    <td style={td}>{p.unique_users}</td>
                                    <td style={td}>{p.total_minutes}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>
        </main>
    );
}

function Card({ label, value }: { label: string; value: number | string }) {
    return (
        <div style={{ padding: 12, border: '1px solid #cfc9b7', background: '#fff' }}>
            <div style={{ fontFamily: 'Newsreader,serif', fontSize: 26, fontWeight: 700 }}>{value}</div>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, color: '#666', marginTop: 2 }}>{label}</div>
        </div>
    );
}

const adminNav: React.CSSProperties = {
    display: 'flex', gap: 12, padding: '10px 0 6px', marginBottom: 16,
    borderBottom: '1px solid #cfc9b7', fontFamily: '-apple-system, sans-serif',
    fontSize: 11, textTransform: 'uppercase', letterSpacing: 2,
};
const navLink: React.CSSProperties = {
    color: '#111', textDecoration: 'none', padding: '4px 10px',
    border: '1px solid transparent', fontWeight: 700,
};
const cardsGrid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginTop: 8 };
const block: React.CSSProperties = { padding: 14, border: '1px solid #cfc9b7', background: '#fff', marginTop: 18 };
const h2: React.CSSProperties = { margin: 0, marginBottom: 10, fontFamily: '-apple-system, sans-serif', fontSize: 11, textTransform: 'uppercase', letterSpacing: 2.5, borderBottom: '1px solid var(--ink)', paddingBottom: 4 };
const th: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid #cfc9b7' };
const td: React.CSSProperties = { padding: '6px 8px' };
function segBtn(active: boolean): React.CSSProperties {
    return {
        padding: '2px 8px', fontSize: 11, cursor: 'pointer',
        border: '1px solid ' + (active ? 'var(--ink)' : '#ccc'),
        background: active ? 'var(--ink)' : '#fff',
        color: active ? '#fff' : '#333',
        fontFamily: '-apple-system, sans-serif',
    };
}
